import {
  Component,
  input,
  computed,
  inject,
  effect, signal,
  ChangeDetectionStrategy
} from '@angular/core';
import {BaseInputComponent, InputComponent} from "../../base/input/input.component";
import {ControlContainer, FormControl, FormGroupDirective, ReactiveFormsModule} from "@angular/forms";
import {NgxMaskDirective} from "ngx-mask";
import {ActionType} from "../../../models/action.type";

@Component({
  selector: 'app-lib-text-input',
  imports: [
    InputComponent,
    ReactiveFormsModule,
    NgxMaskDirective
  ],
  templateUrl: './text-input.component.html',
  styleUrl: './text-input.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  viewProviders: [{provide: ControlContainer, useExisting: FormGroupDirective}]
})
export class TextInputComponent<T extends string | number = string> extends BaseInputComponent {

  mask = input<string | undefined>(undefined);

  parentForm = inject(FormGroupDirective, {optional: true});
  autocompleteType = input<AutoFill | undefined>(undefined);
  label = input<string | null>(null);
  tooltip = input<string | null>(null);
  value = input<T | null>(null);
  disabled = input<boolean>(false);
  readonly = input<boolean>(false);
  required = input<boolean>(true);
  requiredIndicator = input<boolean>(true);
  optionalIndicator = input<boolean>(false);
  type = input<'text' | 'email' | 'password' | 'number' | 'phone'>('text');
  placeholder = input<string>('');
  control = input<FormControl | string | null>(null);
  formControl = computed(() => {
    const control = this.control();
    const parentForm = this.parentForm?.form;
    if (control instanceof FormControl) {
      return control;
    }
    if (typeof control === 'string') {
      return parentForm?.get(control) as FormControl;
    }
    return null;
  });
  formControlName = computed(() => {
    const control = this.control();
    if (typeof control === 'string') {
      return control;
    }
    return null;
  });
  /** The DOM coerces a native `<input>.value` to a DOMString: `null` becomes `''` and a number
   *  becomes its decimal form. Doing that coercion here changes nothing at runtime -- but Angular 21
   *  type-checks this component's own template with `T` left generic, so binding `T | null` to a
   *  `string` property is now an error where earlier versions let it through unexamined. */
  protected valueText = computed(() => String(this.value() ?? ''));

  protected valid = signal<boolean>(false);

  constructor() {
    super();
    effect(() => {
      const control = this.formControl();
      const sub = control?.valueChanges?.subscribe(() => {
        this.valid.set(control!.valid);
      });
      return () => sub?.unsubscribe();
    });
  }


  protected readonly ActionType = ActionType;
}
